import "./styles.assessment.scss";
import { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import {
  MemberAnswerInfo,
  MemberFormInfo,
} from "clinical-forms-experience-client";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import {
  formatDate,
  formatDateLong,
  formatTime,
} from "../../helper/dateFormatter";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { useNavigate } from "react-router-dom";
import { toTitleCase } from "../../helper/TextUtils";
import { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { communicationApi } from "../../api-helpers/CommunicationApiHelper";
import JobAidModal from "../Appointment/JobAidModal";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import {
  PatientGoals,
  PatientIntervention,
  PatientPgiMapResponse,
} from "grid-experience-client";
import { getAllMemberForms, getMemberFormAnswers, getOneMemberForm, getPgisForMemberForm, useAssessmentParams } from "./apiUtils.assessment";
import { getScoreTrend } from "./utils.assessment";
import ClinicalFormDetailsView from "./Details.clinical-forms";

const CompletedAssessments = () => {
  const [memberForm, setMemberForm] = useState<MemberFormInfo | null>(null);
  const [otherMemberForms, setOtherMemberForms] = useState<MemberFormInfo[] | null>(null);
  const [pgis, setPgis] = useState<PatientPgiMapResponse[]>([]);
  const [answers, setAnswers] = useState<MemberAnswerInfo[]>();
  const [scoreMovement, setScoreMovement] = useState<number | null>(0);
  const [scoreTrend, setScoreTrend] = useState<string | null>(null);
  const [pamLevel, setPamLevel] = useState<number | null>(0);
  const [showJobAidModal, setShowJobAidModal] = useState(false);
  const [jobAidUrl, setJobAidUrl] = useState<string>("");
  const [showViewAllButton, setShowViewAllButton] = useState(false);
  const [interventionImages, setInterventionImages] = useState<Map<string, string>>(new Map());

  const {patientId, assessmentTypeId} = useAssessmentParams();
  const pamTitle = "Patient Activation Measure (PAM)";

  const clinicalformsApi = createClinicalFormsApi();
  const commApi = communicationApi();
  const gridApi = createGridApi();

  const navigate = useNavigate();

  const handleViewAllClick = () => {
    navigate(`/patients/${patientId}/assessments/${assessmentTypeId}/resume/${memberForm!.id}?editPgis=true&pastAssessment=true`);
  };

  const handleShowJobAidModal = async (imageLocation: string) => {
    if (imageLocation === "") {
      setShowJobAidModal(false);
      return;
    }
    try {
      const communicationResponse =
      await commApi.communicationControllerGetSignedUrl(imageLocation);
      setJobAidUrl(communicationResponse.data);
      setShowJobAidModal(true);
    } catch {
      console.error("Error fetching signed URL for job aid image:");
    }
  };

  const getPgi = async () => {
    LoadingHelper.showLoader("main");
    try {
      const pgiResponse = await gridApi.gridControllerGetPatientPgiMap(
        patientId!,
        memberForm?.id!
      );
      const pgiResult = pgiResponse.data;
      setPgis(pgiResult);
    } catch (error) {
      console.log(error);
    } finally {
      LoadingHelper.hideLoader("main");
    }
  };

  const loadInterventionImages = async () => {
    LoadingHelper.showLoader("main");
    updateScoreMovement();
    try {
      const pgisResponse = await getPgisForMemberForm(memberForm?.id!);
      const data = pgisResponse?.data || []
      const newInterventionImages = new Map<string, string>();
      if (data.length > 0) {
        data.forEach((pgi) => {
          pgi.goals.forEach((goal) => {
            goal.interventions.forEach((intervention) => {
              if (intervention.imageLocation && intervention.id) {
                newInterventionImages.set(
                  intervention.id,
                  intervention.imageLocation
                );
              }
            });
          });
        });
        setInterventionImages(newInterventionImages);
      }
    } finally {
      LoadingHelper.hideLoader("main");
    }
  };

  const loadAnswers = async (memberFormId: string) => {
    LoadingHelper.showLoader("main");
    const answers = (await getMemberFormAnswers(memberFormId))?.data;
    if (answers){
      setAnswers(answers.formAnswers);
    }
    LoadingHelper.hideLoader("main");
  };

  const pastAssessments = async () => {
    LoadingHelper.showLoader("main");
    const getlastros = await getAllMemberForms(patientId!)
    const sortedSubmittedForms = getlastros?.data.
    filter((form) => form.status === ClinicalFormsConstants.submittedStatus &&
        form.formId === assessmentTypeId)
    .sort((a, b) => {
      return (
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
      );
    });

    if (sortedSubmittedForms?.length) {
      loadAnswers(sortedSubmittedForms[0].id)
      const memberForm = (await getOneMemberForm(sortedSubmittedForms[0].id))
        ?.data;
      setMemberForm(memberForm || null);
    }
    setOtherMemberForms(
      getlastros?.data.filter(
        (form) =>
          form.formId === assessmentTypeId &&
          form.status !== ClinicalFormsConstants.draftStatus
      ) || null
    );
    LoadingHelper.hideLoader("main");
  };

  const updateScoreMovement = () => {
    if (!memberForm || !otherMemberForms) {
      return;
    }

    setScoreMovement(null);
    setScoreTrend(null);
    const currentScore = memberForm.score;
    if (!currentScore) {
      return;
    }
    otherMemberForms.forEach((form, index) => {
      if (form.id === memberForm.id) {
        if (index === otherMemberForms.length - 1) {
          return;
        }

        let lastIndex = index + 1;
        while (lastIndex < otherMemberForms.length) {
          const previousForm = otherMemberForms[lastIndex];
          console.log(lastIndex, currentScore, previousForm.score);
          if (previousForm.score) {
            const delta = currentScore - previousForm.score;
            setScoreMovement(delta);
            const trend = getScoreTrend(memberForm.title || "", currentScore, previousForm.score);
            setScoreTrend(trend);
            return;
          }
          lastIndex++;
        }
      }
    });
  };

  const navigateToSource = () => {
    if (memberForm?.source === "appointment") {
      navigate(`/schedule/charting/${memberForm.sourceRefId}`);
    }
  };

  const getPamLevel = async () => {
    const pamLevel = await clinicalformsApi.clinicalFormsControllerGetLevel(
      memberForm?.id!
    );
    setPamLevel(pamLevel.data);
  };

  useEffect(() => {
    pastAssessments();
  }, []);

  useEffect(() => {
    if (!memberForm) {
      return;
    }

    if(memberForm.title === pamTitle){
      getPamLevel();
    }

    const assessmentFormID = otherMemberForms && otherMemberForms.length > 0
      ? otherMemberForms[0]?.id
      : "";
    setShowViewAllButton(assessmentFormID === memberForm.id);
    loadAnswers(memberForm.id);
    getPgi();
    loadInterventionImages();
    updateScoreMovement();
  }, [memberForm]);

  return (
    <>
      <JobAidModal
        jobAidUrl={jobAidUrl}
        showModal={showJobAidModal}
        handleModal={() => setShowJobAidModal(!showJobAidModal)}
      />
      <div className="assessment-details-view assessment">
        <div className="assessment__aside px-2">
          <div className="border rounded">
            <div className="table-responsive">
              <table className="table fs-12 m-0">
                <thead>
                  <tr>
                    <th className="bg-teal-lighter" scope="col">
                      Sr.No
                    </th>
                    <th className="bg-teal-lighter" scope="col">
                      Assessment
                    </th>
                    <th className="bg-teal-lighter" scope="col">
                      Date
                    </th>
                    <th className="bg-teal-lighter" scope="col">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {otherMemberForms?.map((form, index) => (
                    <tr
                      key={form.id}
                      className={
                        form.id === memberForm?.id
                          ? "member-forms-row member-forms-selected-row"
                          : "member-forms-row"
                      }
                      onClick={() => setMemberForm(form)}
                    >
                      <th scope="row">{index + 1}</th>
                      <td>{form.title}</td>
                      <td>{formatDate(form.createdOn)}</td>
                      <td>{form.score ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="assessment__main px-2">
          <div className="px-4 py-3 border rounded-3">
            <div className="fs-12 text-light text-uppercase">Assessment</div>
            <strong className="fs-16">{memberForm?.title}</strong>
            <hr className="mt-2" />
            <div className="d-flex mt-2">
              <div className="flex-fill">
                <ul className="ap-list">
                  <li className="mt-0 mb-2">
                    <strong>Coach: {memberForm?.updatedBy}</strong>
                  </li>
                  <li className="my-2">
                    <span className="circle-icon bg-teal-light border-transparent me-3">
                      <span className="material-symbols-outlined menu__icon">
                        calendar_today
                      </span>
                    </span>{" "}
                    {formatDateLong(memberForm?.createdOn)}
                  </li>
                  <li className="my-2">
                    <span className="circle-icon bg-teal-light border-transparent me-3">
                      <span className="material-symbols-outlined menu__icon">
                        schedule
                      </span>
                    </span>
                    {formatTime(memberForm?.createdOn)}
                  </li>
                  <li className="my-2">
                    <span className="me-3">Source:</span>
                    <span className="d-block">
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill px-5"
                        type="button"
                        onClick={navigateToSource}
                      >
                        {toTitleCase(memberForm?.source)}
                      </button>
                    </span>
                  </li>
                </ul>
              </div>
              <div>
                <div className="d-flex align-items-center py-2 px-4 border rounded-3">
                  <div>
                    <strong className="fs-14">Score</strong>
                  </div>
                  <div className="px-2">
                    <span className="circle-icon circle-icon-md bg-teal-light fs-22 border-0">
                      {memberForm?.score ?? "-"}
                    </span>
                  </div>
                  {(scoreMovement ?? 0) !== 0 && scoreTrend && (
                    <div>
                      <div className="d-flex align-items-top fs-11">
                        <span
                          className={
                            "material-symbols-outlined align-text-top " +
                            (scoreTrend === "improving"
                              ? "text-success"
                              : "text-warning")
                          }
                        >
                          {scoreTrend === "improving"
                            ? "arrow_upward"
                            : "arrow_downward"}
                        </span>
                        <span
                          className={
                            scoreTrend === "improving"
                              ? "text-success"
                              : "text-warning"
                          }
                        >
                          {(scoreMovement ?? 0) !== 0 && (
                            <span>{scoreMovement! > 0 ? "+" : "-"}</span>
                          )}{" "}
                          {Math.abs(scoreMovement!).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {memberForm?.title === pamTitle ? (
                  <span className="d-block badge bg-low fs-13">
                    PAM Level: <strong>{pamLevel}</strong>
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="assessment-middle">
            {memberForm && (
              <ClinicalFormDetailsView
                formId={memberForm.formId}
                memberFormId={memberForm.id}
                comments={memberForm.comments ?? ""}
                readOnly={true}
                answers={answers}
                hideComments={true}
              />
            )}
          </div>
        </div>
        <div className="assessment__aside assessment__aside-lg pgis-view px-2">
          {memberForm?.status !== ClinicalFormsConstants.discardedStatus && (
            <div className="border rounded">
              <div className="pgi-table fs-12">
                <>
                  <div className="row">
                    <div className="col-12 d-flex justify-content-between align-items-center">
                      <strong className="d-block py-2 px-3 fs-16 border-bottom">
                        Selected PGIs
                      </strong>
                      {showViewAllButton && (
                        <button
                          className="btn btn-sm btn-outline-secondary px-3 rounded-pill me-2"
                          onClick={handleViewAllClick}
                        >
                          View All
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="d-flex bg-gray-FAF rounded rounded-top fw-bold">
                    <div className="flex-fill py-2 px-3">
                      <strong>PROBLEM - GOAL - INTERVENTION</strong>
                    </div>
                  </div>
                  {pgis.length === 0 ? (
                    <div className="text-center py-3">
                      <strong>No PGIs were selected</strong>
                    </div>
                  ) : (
                    <Accordion defaultActiveKey={[]} alwaysOpen>
                      {pgis.map(
                        (pgi: PatientPgiMapResponse, index: number) => {
                          return (
                            <Accordion.Item
                              key={index}
                              eventKey={`problem-${index}`}
                            >
                              <div className="d-flex align-items-stretch border-top fs-12 problem-heading">
                                <div className="w-flex-lg flex-fill py-2 px-4 border-start">
                                  <Accordion.Header>
                                    <span
                                      data-bs-toggle="collapse"
                                      className="accordion-link"
                                    >
                                      <strong className="d-block text-dark fw-bold">
                                        Problem {index + 1}:{" "}
                                        {pgi.problem?.problem}
                                      </strong>
                                      <span className="d-block fs-11 text-light">
                                        {pgi.problem?.problemDesc}
                                      </span>
                                    </span>
                                  </Accordion.Header>
                                </div>
                              </div>
                              <Accordion.Body className="p-0">
                                {pgi.goal &&
                                  pgi.goal.map(
                                    (
                                      goal: PatientGoals,
                                      goalIndex: number
                                    ) => (
                                      <Accordion.Item
                                        key={goalIndex}
                                        eventKey={`problem-${index}__goal-${goalIndex}`}
                                      >
                                        <div className="d-flex align-items-stretch border-top fs-12">
                                          <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 ps-12 border-start">
                                            <div className="flex-fill ps-1">
                                              <div>
                                                <Accordion.Header>
                                                  <span className="accordion-link">
                                                    <strong className="d-inline-block me-2">
                                                      <span className="text-light-black fw-600">
                                                        Goal {goalIndex + 1}:{" "}
                                                        {goal.goal?.goal}
                                                      </span>
                                                    </strong>
                                                  </span>
                                                </Accordion.Header>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <Accordion.Body className="p-0">
                                          {goal &&
                                            goal.interventions.map(
                                              (
                                                intervention: PatientIntervention,
                                                interventionIndex: number
                                              ) => (
                                                <div
                                                  key={interventionIndex}
                                                  className="d-flex align-items-stretch border-top fs-12"
                                                >
                                                  <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 border-start ps-12">
                                                    <div className="flex-fill ps-6">
                                                      <div>
                                                        <span
                                                          className={`material-symbols-outlined me-1 align-text-top fs-20 text-secondary cursor-pointer ${
                                                            interventionImages.has(
                                                              intervention.intervention!
                                                                .interventionId!
                                                            )
                                                              ? "visible"
                                                              : "invisible"
                                                          }`}
                                                          onClick={() =>
                                                            handleShowJobAidModal(
                                                              interventionImages.get(
                                                                intervention!
                                                                  .intervention!
                                                                  .interventionId!
                                                              )!
                                                            )
                                                          }
                                                        >
                                                          quick_reference
                                                        </span>

                                                        <span className="intervention-badge badge badge-offwhite me-1 border">
                                                          Intervention
                                                        </span>
                                                        <span className="me-2 text-light-black">
                                                          {
                                                            intervention
                                                              .intervention
                                                              ?.intervention
                                                          }
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    )
                                  )}
                              </Accordion.Body>
                            </Accordion.Item>
                          );
                        }
                      )}
                    </Accordion>
                  )}
                </>
              </div>
            </div>
          )}
        </div>

        <div className="assessment__footer">
          <div className="text-light fs-12">
            <button
              className="btn btn-outline-primary btn-sm rounded-pill ms-2 px-5"
              type="button"
              onClick={() => navigate(`/patients/${patientId}/assessments`)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompletedAssessments;
